import React, { useEffect, useState, useRef } from 'react';
import { PIPELINE_STAGES, TaskStatusResponse, PhaseStatus } from '../types';
import { getTaskStatus } from '../services/api';
import './TaskStatus.css';

interface TaskStatusProps {
  taskId: string;
}

// Helper function to get task order based on pipeline stages
// Unused but may be needed in future, so keep with eslint disable
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getTaskOrder = (taskType: string): number => {
  switch (taskType) {
    case 'genesis': return 0;
    case 'process': return 1;
    case 'chunk_plan': return 2;
    case 'chunk_execute': return 3;
    case 'upload': return 4;
    case 'extract': return 5;
    case 'embedding': 
    case 'embed': return 6;
    case 'db_load': 
    case 'vectordb': return 7;
    default: return 999; // Any unknown task types
  }
};

// Helper function to get a color for each task type
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getTaskTypeColor = (taskType: string): string => {
  switch (taskType) {
    case 'genesis': return '#4285f4'; // Google blue
    case 'process': return '#34a853'; // Google green
    case 'chunk_plan': return '#fbbc05'; // Google yellow
    case 'chunk_execute': return '#ea4335'; // Google red
    case 'upload': return '#673ab7'; // Purple
    case 'extract': return '#3f51b5'; // Indigo
    case 'embedding': 
    case 'embed': return '#009688'; // Teal
    case 'db_load':
    case 'vectordb': return '#ff5722'; // Deep Orange
    default: return '#5f6368'; // Google grey
  }
};

// Get a nice formatted display name for the task type
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getTaskTitle = (taskType: string): string => {
  return PIPELINE_STAGES[taskType]?.title || taskType.split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Calculate progress percentage for a task
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const calculateProgress = (task: any): number => {
  if (!task.total_items || task.total_items === 0) return 0;
  return Math.round((task.succeeded_items || 0) / task.total_items * 100);
};

// Calculate progress percentage for a phase
const calculatePhaseProgress = (phase: PhaseStatus): number => {
  if (!phase.total || phase.total === 0) return 0;
  return Math.round((phase.status.completed || 0) / phase.total * 100);
};

// Calculate overall progress for all phases
const calculateOverallProgress = (taskStatus: TaskStatusResponse): number => {
  const { phases } = taskStatus;
  const totalItems = phases.x.total + phases.y.total + phases.z.total;
  const completedItems = phases.x.status.completed + phases.y.status.completed + phases.z.status.completed;
  
  if (totalItems === 0) return 0;
  return Math.round((completedItems / totalItems) * 100);
};

// Determine if a task has any warnings (failures but still progressing)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const hasWarnings = (task: any): boolean => {
  return !!task.failed_items && task.failed_items > 0 && task.status !== 'failed';
};

// Determine if a phase has any warnings (failures but still progressing)
const hasPhaseWarnings = (phase: PhaseStatus): boolean => {
  return (phase.status.failed > 0 || phase.status.perm_failed > 0);
};

// Format attempt distribution for display
const formatAttempts = (attempts: Record<string, number>): string => {
  return Object.entries(attempts)
    .map(([attempt, count]) => `${count} items with ${attempt} ${parseInt(attempt) === 1 ? 'attempt' : 'attempts'}`)
    .join(', ');
};

const TaskStatus: React.FC<TaskStatusProps> = ({ taskId }) => {
  const [taskStatus, setTaskStatus] = useState<TaskStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDetails, setShowDetails] = useState<{x: boolean, y: boolean, z: boolean}>({x: false, y: false, z: false});
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const response = await getTaskStatus(taskId);
        setTaskStatus(response);
        setIsLoading(false);
      } catch (err: any) {
        setError(err.message || 'Failed to load task status');
        setIsLoading(false);
      }
    };

    // Initial fetch
    fetchTaskStatus();

    // Set up polling every 3 seconds
    pollingInterval.current = setInterval(fetchTaskStatus, 3000);

    // Clean up the interval on unmount
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [taskId]);

  const toggleDetails = (phase: 'x' | 'y' | 'z') => {
    setShowDetails(prev => ({
      ...prev,
      [phase]: !prev[phase]
    }));
  };

  if (isLoading) {
    return <div className="loading">Loading task...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  if (!taskStatus) {
    return <div className="error-message">Task not found</div>;
  }

  const { phases } = taskStatus;
  
  // Determine overall status
  const xCompleted = phases.x.status.completed === phases.x.total;
  const yCompleted = phases.y.status.completed === phases.y.total;
  const zCompleted = phases.z.status.completed === phases.z.total;
  const allCompleted = xCompleted && yCompleted && zCompleted;
  
  const hasFailed = 
    phases.x.status.perm_failed > 0 || 
    phases.y.status.perm_failed > 0 || 
    phases.z.status.perm_failed > 0;
  
  const isInProgress = 
    phases.x.status.in_flight > 0 || 
    phases.y.status.in_flight > 0 || 
    phases.z.status.in_flight > 0 ||
    !allCompleted;
  
  let overallStatus = 'pending';
  if (hasFailed && !allCompleted) overallStatus = 'failed';
  else if (allCompleted) overallStatus = 'completed';
  else if (isInProgress) overallStatus = 'in_progress';
  
  // Calculate total failures
  const totalFailures = 
    phases.x.status.failed + phases.x.status.perm_failed +
    phases.y.status.failed + phases.y.status.perm_failed +
    phases.z.status.failed + phases.z.status.perm_failed;
  
  // Calculate overall progress
  const progressPercentage = calculateOverallProgress(taskStatus);
  
  return (
    <div className="task-status">
      <h3>Task Status</h3>
      
      <div className="task-summary">
        <div className="task-header">
          <div>
            <p><strong>Task ID:</strong> {taskStatus.task_id}</p>
            <p><strong>Created:</strong> {new Date(taskStatus.created_at).toLocaleString()}</p>
            <p><strong>Last Updated:</strong> {new Date(taskStatus.updated_at).toLocaleString()}</p>
            {taskStatus.task_hint && (
              <p><strong>Task Hint:</strong> {taskStatus.task_hint}</p>
            )}
            {totalFailures > 0 && (
              <p className="warning-text"><strong>Warning:</strong> {totalFailures} items failed processing</p>
            )}
          </div>
          <div className={`status-badge status-${overallStatus}`}>
            {overallStatus === 'in_progress' ? 'In Progress' : 
              overallStatus.charAt(0).toUpperCase() + overallStatus.slice(1)}
          </div>
        </div>
        
        <div className="progress-container">
          <div className="progress-label">
            <span>Overall Progress: {progressPercentage}%</span>
            <span>
              {phases.x.status.completed + phases.y.status.completed + phases.z.status.completed} of {phases.x.total + phases.y.total + phases.z.total} items processed
            </span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ 
                width: `${progressPercentage}%`,
                backgroundColor: hasFailed ? '#ea4335' : '#34a853'
              }}
            ></div>
          </div>
        </div>
      </div>
      
      <h4 className="pipeline-header">
        Processing Pipeline
      </h4>
      
      <div className="task-list">
        {/* Phase X */}
        <div 
          className={`task-item ${xCompleted ? 'completed' : ''} 
                    ${phases.x.status.in_flight > 0 ? 'in-progress' : ''} 
                    ${hasPhaseWarnings(phases.x) ? 'warning' : ''}`}
        >
          <div className="task-item-header" onClick={() => toggleDetails('x')} style={{ cursor: 'pointer' }}>
            <div 
              className="task-icon"
              style={{ backgroundColor: '#4285f4' }}
            >
              1
            </div>
            <div className="task-details">
              <h4>File Processing {showDetails.x ? '▼' : '▶'}</h4>
              <p>Downloading and chunking files</p>
            </div>
            <div className="task-status-icon">
              {phases.x.status.completed === phases.x.total ? (
                <span className="status-icon completed">✓</span>
              ) : phases.x.status.failed > 0 || phases.x.status.perm_failed > 0 ? (
                <span className="status-icon warning">⚠</span>
              ) : phases.x.status.in_flight > 0 ? (
                <span className="status-icon in-progress">⟳</span>
              ) : (
                <span className="status-icon pending">⧖</span>
              )}
            </div>
          </div>
          <div className="task-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${calculatePhaseProgress(phases.x)}%`,
                  backgroundColor: hasPhaseWarnings(phases.x) ? '#fbbc05' : '#4285f4'
                }}
              ></div>
            </div>
            <div className="progress-details">
              <span>{calculatePhaseProgress(phases.x)}%</span>
              <span>{phases.x.status.completed} of {phases.x.total} complete</span>
              {phases.x.status.failed > 0 && <span className="warning">{phases.x.status.failed} failed</span>}
            </div>
          </div>
          
          {showDetails.x && (
            <div className="phase-details">
              <div className="detail-section">
                <h5>Status Breakdown</h5>
                <ul>
                  <li>Pending: {phases.x.status.pending}</li>
                  <li>In Progress: {phases.x.status.in_flight}</li>
                  <li>Completed: {phases.x.status.completed}</li>
                  <li>Failed (may retry): {phases.x.status.failed}</li>
                  <li>Permanently Failed: {phases.x.status.perm_failed}</li>
                </ul>
              </div>
              
              <div className="detail-section">
                <h5>Attempt Distribution</h5>
                <p>{formatAttempts(phases.x.attempts)}</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Phase Y */}
        <div 
          className={`task-item ${yCompleted ? 'completed' : ''} 
                    ${phases.y.status.in_flight > 0 ? 'in-progress' : ''} 
                    ${hasPhaseWarnings(phases.y) ? 'warning' : ''}`}
        >
          <div className="task-item-header" onClick={() => toggleDetails('y')} style={{ cursor: 'pointer' }}>
            <div 
              className="task-icon"
              style={{ backgroundColor: '#34a853' }}
            >
              2
            </div>
            <div className="task-details">
              <h4>Chunk Processing {showDetails.y ? '▼' : '▶'}</h4>
              <p>Gemini AI extraction</p>
            </div>
            <div className="task-status-icon">
              {phases.y.status.completed === phases.y.total ? (
                <span className="status-icon completed">✓</span>
              ) : phases.y.status.failed > 0 || phases.y.status.perm_failed > 0 ? (
                <span className="status-icon warning">⚠</span>
              ) : phases.y.status.in_flight > 0 ? (
                <span className="status-icon in-progress">⟳</span>
              ) : (
                <span className="status-icon pending">⧖</span>
              )}
            </div>
          </div>
          <div className="task-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${calculatePhaseProgress(phases.y)}%`,
                  backgroundColor: hasPhaseWarnings(phases.y) ? '#fbbc05' : '#34a853'
                }}
              ></div>
            </div>
            <div className="progress-details">
              <span>{calculatePhaseProgress(phases.y)}%</span>
              <span>{phases.y.status.completed} of {phases.y.total} complete</span>
              {phases.y.status.failed > 0 && <span className="warning">{phases.y.status.failed} failed</span>}
            </div>
          </div>
          
          {showDetails.y && (
            <div className="phase-details">
              <div className="detail-section">
                <h5>Status Breakdown</h5>
                <ul>
                  <li>Pending: {phases.y.status.pending}</li>
                  <li>In Progress: {phases.y.status.in_flight}</li>
                  <li>Completed: {phases.y.status.completed}</li>
                  <li>Failed (may retry): {phases.y.status.failed}</li>
                  <li>Permanently Failed: {phases.y.status.perm_failed}</li>
                </ul>
              </div>
              
              <div className="detail-section">
                <h5>Attempt Distribution</h5>
                <p>{formatAttempts(phases.y.attempts)}</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Phase Z */}
        <div 
          className={`task-item ${zCompleted ? 'completed' : ''} 
                    ${phases.z.status.in_flight > 0 ? 'in-progress' : ''} 
                    ${hasPhaseWarnings(phases.z) ? 'warning' : ''}`}
        >
          <div className="task-item-header" onClick={() => toggleDetails('z')} style={{ cursor: 'pointer' }}>
            <div 
              className="task-icon"
              style={{ backgroundColor: '#ea4335' }}
            >
              3
            </div>
            <div className="task-details">
              <h4>Extraction Processing {showDetails.z ? '▼' : '▶'}</h4>
              <p>Creating embeddings</p>
            </div>
            <div className="task-status-icon">
              {phases.z.status.completed === phases.z.total ? (
                <span className="status-icon completed">✓</span>
              ) : phases.z.status.failed > 0 || phases.z.status.perm_failed > 0 ? (
                <span className="status-icon warning">⚠</span>
              ) : phases.z.status.in_flight > 0 ? (
                <span className="status-icon in-progress">⟳</span>
              ) : (
                <span className="status-icon pending">⧖</span>
              )}
            </div>
          </div>
          <div className="task-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${calculatePhaseProgress(phases.z)}%`,
                  backgroundColor: hasPhaseWarnings(phases.z) ? '#fbbc05' : '#ea4335'
                }}
              ></div>
            </div>
            <div className="progress-details">
              <span>{calculatePhaseProgress(phases.z)}%</span>
              <span>{phases.z.status.completed} of {phases.z.total} complete</span>
              {phases.z.status.failed > 0 && <span className="warning">{phases.z.status.failed} failed</span>}
            </div>
          </div>
          
          {showDetails.z && (
            <div className="phase-details">
              <div className="detail-section">
                <h5>Status Breakdown</h5>
                <ul>
                  <li>Pending: {phases.z.status.pending}</li>
                  <li>In Progress: {phases.z.status.in_flight}</li>
                  <li>Completed: {phases.z.status.completed}</li>
                  <li>Failed (may retry): {phases.z.status.failed}</li>
                  <li>Permanently Failed: {phases.z.status.perm_failed}</li>
                </ul>
              </div>
              
              <div className="detail-section">
                <h5>Attempt Distribution</h5>
                <p>{formatAttempts(phases.z.attempts)}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskStatus; 